import { useCallback, useMemo } from "react";
import {
  CustomFilterProps,
  CustomFilterCallbacks,
  useGridFilter,
} from "ag-grid-react";
import { IDoesFilterPassParams } from "ag-grid-community";
import { RawAddressWithIndices } from "./types";

type SetFilterModel = string[];

type ValueCount = {
  value: string;
  count: number;
};

const BLANK_LABEL = "(Blank)";

const normalizeValue = (val: unknown): string => {
  if (val == null || val === "") return BLANK_LABEL;
  return String(val);
};

export const SetFilter = (
  props: CustomFilterProps<RawAddressWithIndices, unknown, SetFilterModel>
) => {
  const { model, onModelChange, api } = props;

  // Compute unique values and their counts from all row data
  const valueCounts: ValueCount[] = useMemo(() => {
    const counts = new Map<string, number>();
    api.forEachNode((node) => {
      if (!node.data) return;
      const raw = props.getValue(node);
      const val = normalizeValue(raw);
      counts.set(val, (counts.get(val) ?? 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => {
        // Blank always last
        if (a.value === BLANK_LABEL) return 1;
        if (b.value === BLANK_LABEL) return -1;
        // Most frequent first
        return b.count - a.count || a.value.localeCompare(b.value);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  }, [api, props.getValue]);

  const allValues = useMemo(
    () => valueCounts.map((vc) => vc.value),
    [valueCounts]
  );

  // Selected values: if model is null (no filter), all are selected
  const selectedValues = useMemo(
    () => new Set<string>(model ?? allValues),
    [model, allValues]
  );

  const allSelected = selectedValues.size === allValues.length;

  const doesFilterPass = useCallback(
    (params: IDoesFilterPassParams<RawAddressWithIndices>) => {
      if (!model) return true;
      const raw = props.getValue(params.node);
      const val = normalizeValue(raw);
      return selectedValues.has(val);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
    [model, selectedValues, props.getValue]
  );

  const filterCallbacks: CustomFilterCallbacks = useMemo(
    () => ({
      doesFilterPass,
    }),
    [doesFilterPass]
  );

  useGridFilter(filterCallbacks);

  const toggleValue = (value: string) => {
    const next = new Set(selectedValues);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    // If all values are selected, clear the filter (null model)
    if (next.size === allValues.length) {
      onModelChange(null);
    } else {
      onModelChange(Array.from(next));
    }
  };

  const toggleAll = () => {
    if (allSelected) {
      // Deselect all
      onModelChange([]);
    } else {
      // Select all — clear filter
      onModelChange(null);
    }
  };

  return (
    <div
      style={{
        padding: "8px",
        minWidth: "200px",
        maxHeight: "300px",
        display: "flex",
        flexDirection: "column",
        fontSize: "13px",
      }}
    >
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "4px 0",
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        <input
          type="checkbox"
          checked={allSelected}
          onChange={toggleAll}
          ref={(el) => {
            if (el) {
              el.indeterminate = selectedValues.size > 0 && !allSelected;
            }
          }}
        />
        Select All
      </label>
      <hr
        style={{
          margin: "4px 0",
          border: "none",
          borderTop: "1px solid var(--surface-border, #ddd)",
        }}
      />
      <div style={{ overflowY: "auto", flex: 1 }}>
        {valueCounts.map(({ value, count }) => (
          <label
            key={value}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "3px 0",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={selectedValues.has(value)}
              onChange={() => toggleValue(value)}
            />
            <span style={{ flex: 1 }}>{value}</span>
            <span
              style={{
                color: "var(--text-color-secondary, #888)",
                fontSize: "12px",
              }}
            >
              ({count})
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};
